import {
  type ImageSources,
  MultiValuePropBlock as MultiValuePropBlockSDS,
} from '@snapchat/snap-design-system-marketing';
import { type FC, useContext } from 'react';

import { useContentfulImages } from '../../../../contentful-client/src';
import { AppContext } from '../../AppContext';
import { useMotifScheme } from '../../hooks/useMotifScheme';
import { combineImageSources } from '../../utils/combineImageSources';
import {
  renderRichTextMultiLineWithMarkings,
  renderRichTextWithElementsNoHeadings,
} from '../../utils/renderText/renderRichText';
import { CallToAction } from '../CallToAction';
import { getImageSourceSettings } from '../Image';
import { ImageSubtopic } from './ImageSubtopic';
import { StatSubtopic } from './StatSubtopic';
import { TextSubtopic } from './TextSubtopic';
import type { MultiValuePropBlockProps } from './types';

// Default Image height values for each available Aspect Ratio
const DefaultImageHeights = {
  '3:2': 315,
  '9:16': 656,
  '1:1': 472,
  '16:9': 265,
};

const DefaultMobileImageHeights = {
  '3:2': 180,
  '9:16': 400,
  '1:1': 271,
  '16:9': 152,
};

export const MultiValuePropBlock: FC<MultiValuePropBlockProps> = props => {
  const { getImageSources } = useContentfulImages();
  const { isRTL } = useContext(AppContext);

  const {
    anchor,
    brandBackgroundColor,
    backgroundColor,
    title,
    multiValuePropBlockSubtitle,
    body,
    subtopicsCollection,
    callsToActionCollection,
    mediaCollection,
    desktopMediaAspectRatio,
    mobileMediaAspectRatio,
    mediaDirection,
    autoplayCarousel,
    fullHeight,
    style,
  } = props;

  const motifScheme = useMotifScheme(brandBackgroundColor);

  const callsToAction = callsToActionCollection?.items?.map(item => (
    <CallToAction key={item.sys.id} {...item} />
  ));

  const mediaSources = mediaCollection?.items?.map(item => {
    if (item.__typename === 'Image') {
      const { desktopSettings, mobileSettings } = getImageSourceSettings({
        desktopHeight: item.media?.height ?? 0,
        mobileHeight: item.mobileMedia?.height,
        desktopDefaultHeight: DefaultImageHeights[desktopMediaAspectRatio],
        mobileDefaultHeight: DefaultMobileImageHeights[mobileMediaAspectRatio],
        enableHighDpi: item.enableHighDpi,
        quality: item.quality,
      });

      return {
        type: item.__typename,
        altText: item.media.description,
        sources: combineImageSources({
          desktop: getImageSources(item.media.url, desktopSettings),
          mobile: getImageSources(item.mobileMedia?.url, mobileSettings),
        }),
      };
    }

    if (item.__typename === 'Video') {
      return {
        type: item.__typename,
        autoPlay: !!item.autoPlay,
        videoSource: item.media.url,
        mobileVideoSource: item?.mobileMedia?.url,
        sourceType: item.media.contentType,
      };
    }

    return;
  });

  const subtopics = subtopicsCollection.items;

  let stickers;

  if (mediaCollection?.items?.length === 1) {
    const firstMediaItem = mediaCollection.items[0];

    stickers = firstMediaItem?.stickersCollection?.items?.map(sticker => ({
      imgSrcs: getImageSources(sticker?.asset?.url) as ImageSources,
      position: sticker?.position,
      rotation: sticker?.rotation,
    }));
  }

  return (
    <MultiValuePropBlockSDS
      anchor={anchor}
      motifScheme={motifScheme}
      backgroundColor={backgroundColor}
      title={renderRichTextMultiLineWithMarkings(title)}
      subtitle={multiValuePropBlockSubtitle}
      body={renderRichTextWithElementsNoHeadings(body)}
      autoplayCarousel={autoplayCarousel}
      callsToAction={callsToAction?.length ? callsToAction : undefined}
      mediaSources={mediaSources}
      desktopMediaAspectRatio={desktopMediaAspectRatio}
      mobileMediaAspectRatio={mobileMediaAspectRatio}
      mediaDirection={mediaDirection}
      fullHeight={fullHeight}
      style={style}
      stickers={stickers}
      isRtl={isRTL}
    >
      {subtopics.map(subtopic => {
        if (subtopic.__typename === 'ImageSubtopic') {
          return <ImageSubtopic key={subtopic.sys.id} {...subtopic} />;
        }

        if (subtopic.__typename === 'StatSubtopic') {
          return <StatSubtopic key={subtopic.sys.id} {...subtopic} />;
        }

        if (subtopic.__typename === 'TextSubtopic') {
          return <TextSubtopic key={subtopic.sys.id} {...subtopic} />;
        }

        return null;
      })}
    </MultiValuePropBlockSDS>
  );
};
